import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {AddStructuredTableRowsUsingAIResult} from 'notebook/ai-tools/flexible-data-entry/addStructuredTableRowsUsingAI';
import {ClientId, FromServerId, ToServerId} from 'notebook/ELNConstants';
import {
  BaseGridCreationStructuredTableAdapter,
  GridCreationTableMetadata,
  GridCreationTableRowMetadata,
} from 'notebook/integrations/samples/BaseGridCreationStructuredTableAdapter';
import {getUnfinalizedParentStorablePrimarySample} from 'notebook/integrations/samples/InventoryCapacityValidationFunctions';
import addSamplesFromWorklist from 'notebook/sample-propagation/addSamplesFromWorklist';
import {addToWorklistActionFunction} from 'notebook/structured-tables/action-functions/addToWorklistActionFunction';
import {createPlatesActionFunction} from 'notebook/structured-tables/action-functions/createPlatesActionFunction';
import createPlatesRowsActionFunction from 'notebook/structured-tables/action-functions/createPlatesRowsActionFunction';
import {printBarcodesActionFunction} from 'notebook/structured-tables/action-functions/printBarcodesActionFunction';
import {suggestedPlotsOnlyActionFunction} from 'notebook/structured-tables/action-functions/suggestedPlotsOnlyActionFunction';
import PlateCreationTableRowUpdater from 'notebook/structured-tables/row-updaters/PlateCreationTableRowUpdater';
import {getSpreadsheetTypeForColumn} from 'notebook/structured-tables/spreadsheet-import/getSpreadsheetColumnTypes';
import importRowsFromSpreadsheet from 'notebook/structured-tables/spreadsheet-import/importRowsFromSpreadsheet';
import {
  ActionFunctions,
  BaseStructuredTable,
  PrimarySample,
  PrimarySampleLookupType,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableAddFromSourceType,
  StructuredTableAddOperation,
  StructuredTableColumn,
  StructuredTableColumnSummary,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {getStorableInfoFromTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {StorableInfo} from 'notebook/tables/typed-values/TypedValueTypes';
import {createProcessActionFunction} from 'processes/tables/action-functions/createProcessActionFunction';
import {createRequestV2SubmissionActionFunction} from 'processes/tables/action-functions/createRequestV2SubmissionActionFunction';
import {PlateSchema} from 'samples/storables/StorableSchemaConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import APIUtil from 'util/APIUtil';
import {WORKLIST_FILL_LOCATION} from 'worklists/WorklistConstants';

/**
 * A PlateCreation table contains all of the schema fields needed for
 * a particular plate as well as a name for the plate and an optional
 * barcode column, depending on the settings in the PlateCreationTableMetadata.
 */

export type PlateCreationTableMetadata = GridCreationTableMetadata;

export type PlateCreationTableRowMetadata = GridCreationTableRowMetadata;

export class PlateCreationStructuredTableAdapter extends BaseGridCreationStructuredTableAdapter {
  getDisplayName(_: StructuredTable<PlateCreationTableMetadata, PlateCreationTableRowMetadata>): string {
    return 'Plate Creation';
  }

  /**
   * Returns all supported columns for the schemas provided but always
   * removes the StageRun column, if present.  Additionally, we need
   * to always show the following columns:
   *
   * - Location -- the parent location of the plate to be created
   * - Plate -- the name we want the plate to have
   *
   * Additionally, if the `shouldAutogenerateBarcode` field in the
   * metadata is false, then we will show a Barcode column where the
   * user can enter the barcode they would like the plate to have.
   */
  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: PlateCreationTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const columns = super._getAllSupportedColumns({
      entryId,
      metadata,
      schemas,
      selectorsById,
      structuredTableInsertionContext,
    });
    return [
      ...(!metadata.requireCustomName
        ? [
            {
              ...makeStructuredTableColumn({
                columnType: TableColumnType.PLATE,
                name: 'Plate Name',
                defaultValue: {text: 'New Plate'},
              }),
              isReadOnly: true,
            },
          ]
        : [
            {
              ...makeStructuredTableColumn({
                columnType: TableColumnType.PLATE,
                name: 'Plate Name',
              }),
              isRequired: true,
            },
          ]),
      ...(metadata.shouldAutogenerateBarcode
        ? []
        : [
            {
              ...makeStructuredTableColumn({
                columnType: TableColumnType.STRING,
                name: 'Barcode',
              }),
              isRequired: true,
            },
          ]),
      makeStructuredTableColumn({
        columnType: TableColumnType.PARENT_STORABLE,
        name: 'Parent Location',
        isRequired: true,
      }),
      ...columns,
    ];
  }

  override getActionFunctions(
    tableMetadata: PlateCreationTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<PlateCreationTableMetadata, PlateCreationTableRowMetadata> {
    const inherited = super.getActionFunctions(tableMetadata, schemas);
    return {
      ...inherited,
      primary: [createPlatesActionFunction],
      secondary: [
        // TODO: Figure out if this use of ! is safe.
        ...inherited.secondary!,
        createRequestV2SubmissionActionFunction,
        createProcessActionFunction,
        addToWorklistActionFunction,
        suggestedPlotsOnlyActionFunction,
      ],
      // TODO: Figure out if this use of ! is safe.
      overflow: [...inherited.overflow!, printBarcodesActionFunction],
      contextMenu: [createPlatesRowsActionFunction],
      footer: inherited.footer ?? [],
    };
  }

  // Keep in sync with PlateCreationTableInstantiator.get_primary_sample_ids_for_row (which delegates to
  // GridCreationTableInstantiator._get_storable_creation_primary_sample_ids) in
  // benchling/notebook/structured_tables/instantiators/box_or_plate_creation_table_instantiator.py (tested by
  // TestPlateCreationInstantiatorPrimarySamples in
  // tests/unit/notebook/structured_tables/instantiators/box_or_plate_creation_table_instantiator_test.py)
  override getPrimarySamples(
    structuredTable: StructuredTable<PlateCreationTableMetadata, PlateCreationTableRowMetadata>,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): {[rowId: string]: Array<PrimarySample>} {
    const columnIndex = structuredTable.columns.findIndex(
      ({columnType}) => columnType === TableColumnType.PLATE
    );
    assert(columnIndex !== -1, 'Expected structured table to have a PLATE column ');
    const primarySamplesByRowId: {[rowId: string]: Array<PrimarySample>} = {};
    const schema = structuredTable.schemas[0];

    if (schema == null) {
      // Most likely, the user doesn't have permissions on the schema
      for (const {rowId} of structuredTable.rows) {
        primarySamplesByRowId[rowId] = [];
      }
      return primarySamplesByRowId;
    }

    structuredTable.rows.forEach(({rowId, cells, isFinalized}, rowIndex) => {
      // If row is not finalized, fetch parent storable if present. This is used to refresh the queries
      // for total & occupied capacity that power the cross-row capacity validation
      if (!isFinalized) {
        primarySamplesByRowId[rowId] = getUnfinalizedParentStorablePrimarySample({
          structuredTable,
          computedTable,
          rowIndex,
        });
        return;
      }
      // Plate will only be present in finalized rows, as it has not yet been created in unfinalized ones
      const typedValue =
        computedTable != null ? computedTable[rowIndex][columnIndex]?.value : cells[columnIndex].typedValue;
      const plate: StorableInfo | null = typedValue ? getStorableInfoFromTypedValue(typedValue) : null;
      if (plate && APIUtil.isPlate(plate?.id ?? '')) {
        primarySamplesByRowId[rowId] = [
          {
            schemaId: schema.id,
            columnGroup: null,
            sampleLookupInfo: {sampleType: PrimarySampleLookupType.DIRECT, idForLookup: plate.id},
          },
        ];
      } else {
        primarySamplesByRowId[rowId] = [];
      }
    });
    return primarySamplesByRowId;
  }

  override getTableKindRowUpdater(): typeof PlateCreationTableRowUpdater {
    return PlateCreationTableRowUpdater;
  }

  override getColumnSummariesForCreateProcessShortcut(
    structuredTable: StructuredTable<PlateCreationTableMetadata, PlateCreationTableRowMetadata>
  ): Array<StructuredTableColumnSummary & {columnIndex: number}> {
    // The primary column will always be the plate
    const primaryColumnIndex = structuredTable.columns.findIndex(
      (column) => column.columnType === TableColumnType.PLATE
    );
    assert(primaryColumnIndex !== -1, 'Found plate creation table without a Plate column');
    return [
      {
        validDestinationColumnRequirements: [
          {
            columnType: TableColumnType.TAG_VALUE,
            schemaFieldType: FieldType.STORABLE_LINK,
            folderItemType: null,
          },
          {columnType: TableColumnType.PLATE, schemaFieldType: null, folderItemType: null},
        ],
        schemaIdConstraint: structuredTable.schemas[0]?.id ?? null,
        columnIndex: primaryColumnIndex,
      },
    ];
  }

  override getAddFromSourceOperations(
    entryId: string,
    structuredTable?: PlateCreationStructuredTable,
    addAIDataEntryRows?: (
      initialPromptText?: string,
      initialSelectedTableName?: string,
      result?: AddStructuredTableRowsUsingAIResult | null
    ) => void
  ): Array<StructuredTableAddOperation<PlateCreationTableMetadata, PlateCreationTableRowMetadata>> {
    const ops: Array<StructuredTableAddOperation<PlateCreationTableMetadata, PlateCreationTableRowMetadata>> =
      [
        {
          type: StructuredTableAddFromSourceType.ADD_FROM_SPREADSHEET,
          onSelect: async ({structuredTable, addFullRows}) => {
            const plateSchema = structuredTable.schemas[0] as PlateSchema;
            assert(
              plateSchema != null,
              'Tried to import spreadsheet into plate creation table with inaccessible or nonexistent schema'
            );
            const rows = await importRowsFromSpreadsheet({
              structuredTable,
              schemas: {plateSchema},
              getSpreadsheetTypeForColumn: (column) =>
                getSpreadsheetTypeForColumn(ModelTypeConstants.PLATE_SCHEMA, column),
            });

            if (rows) {
              addFullRows(rows);
            }
          },
        },
      ];
    if (BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_FROM_WORKLIST,
        onSelect: ({structuredTable, addSamples}) => {
          addSamplesFromWorklist(
            structuredTable,
            this,
            addSamples,
            WORKLIST_FILL_LOCATION.PLATE_CREATION_TABLE
          );
        },
      });
    }
    if (BenchlingConfig.ELN_ENABLE_AI_DATA_ENTRY) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_USING_AI,
        onSelect: ({structuredTable}) => {
          addAIDataEntryRows?.(undefined, structuredTable.name);
        },
      });
    }
    return ops;
  }

  override enableCustomColumns(structuredTable: PlateCreationStructuredTable): boolean {
    return true;
  }

  override enableLookupColumns(structuredTable: PlateCreationStructuredTable): boolean {
    return true;
  }
}

export type GenericPlateCreationStructuredTable<TIds> = BaseStructuredTable<
  PlateCreationTableMetadata,
  PlateCreationTableRowMetadata,
  TIds
>;

export type PlateCreationStructuredTable = GenericPlateCreationStructuredTable<ClientId>;
export type FromServerPlateCreationStructuredTable = GenericPlateCreationStructuredTable<FromServerId>;
export type ToServerPlateCreationStructuredTable = GenericPlateCreationStructuredTable<ToServerId>;
